import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

const articles = [
  {
    id: 'interactive-mind-games',
    title: 'Interactive Mind Games: Can Your Brain Be Tricked?',
    excerpt: 'Right now, as you read this, your brain is lying to you. Discover the predictable pathways your mind travels — and how mentalist Simon Nomis turns that into an unforgettable live experience.',
    category: 'Mind Games',
    date: 'July 6, 2026',
    readTime: '15 min read',
    image: 'https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/92df2lj2_simonthought.webp',
    link: '/articles/interactive-mind-games',
  },
  {
    id: 'why-live-magic-beats-streaming',
    title: 'Why Live Magic Beats Streaming Every Time',
    excerpt: 'In a world of screens and algorithms, there\'s something irreplaceable about sitting inches away from the impossible. Discover why audiences are choosing live magic over digital entertainment.',
    category: 'Magic & Entertainment',
    date: 'July 2, 2026',
    readTime: '14 min read',
    image: 'https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg',
    link: '/articles/why-live-magic-beats-streaming',
  },
  {
    id: 'top-5-things-to-do-orlando-this-summer',
    title: 'Top 5 Things to Do in Orlando This Summer (Beyond Theme Parks)',
    excerpt: 'Orlando is more than roller coasters. From immersive magic shows to hidden culinary gems, here are the top experiences locals and visitors are raving about in 2026.',
    category: 'Orlando Guide',
    date: 'June 28, 2026',
    readTime: '8 min read',
    image: 'https://customer-assets.emergentagent.com/job_reality-bender/artifacts/ddh5e384_mastermarquee.jpg',
    link: '/articles/top-5-orlando-summer',
  },
  {
    id: 'what-is-mentalism',
    title: 'What Is Mentalism? The Art Behind the Mind Reading',
    excerpt: 'Mentalism is one of the oldest and most captivating forms of performance art. Learn how mentalists like Simon Nomis blur the line between psychology, intuition, and the seemingly supernatural.',
    category: 'Behind the Curtain',
    date: 'June 20, 2026',
    readTime: '14 min read',
    image: 'https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/92df2lj2_simonthought.webp',
    link: '/articles/what-is-mentalism',
  },
  {
    id: 'perfect-orlando-date-night',
    title: 'How to Plan the Perfect Orlando Date Night',
    excerpt: 'Dinner and a movie? That\'s last decade. Discover why couples across Central Florida are making a live magic show the centerpiece of their most memorable evenings out.',
    category: 'Orlando Guide',
    date: 'June 15, 2026',
    readTime: '12 min read',
    image: 'https://customer-assets.emergentagent.com/job_reality-bender/artifacts/ddh5e384_mastermarquee.jpg',
    link: '/articles/perfect-orlando-date-night',
  },
  {
    id: 'history-of-magic-shows-in-florida',
    title: 'A Brief History of Magic Shows in Florida',
    excerpt: 'From traveling vaudeville acts to world-class residencies, Florida has a rich tradition of live magic. Explore how Orlando became one of America\'s top destinations for illusionists.',
    category: 'Magic & Entertainment',
    date: 'June 8, 2026',
    readTime: '8 min read',
    image: 'https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg',
  },
  {
    id: 'what-to-expect-at-mental-hop-scotch',
    title: 'What to Expect at Mental Hop Scotch: A First-Timer\'s Guide',
    excerpt: 'Heading to your first Mental Hop Scotch show? Here\'s everything you need to know — from what to wear, when to arrive, and why you might walk away with cash in your pocket.',
    category: 'Show Guide',
    date: 'June 1, 2026',
    readTime: '16 min read',
    image: 'https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/92df2lj2_simonthought.webp',
    link: '/articles/first-timers-guide',
  },
];

const categoryColors = {
  'Mind Games': 'bg-purple-600/80 text-purple-100',
  'Magic & Entertainment': 'bg-purple-600/80 text-purple-100',
  'Orlando Guide': 'bg-blue-600/80 text-blue-100',
  'Behind the Curtain': 'bg-amber-600/80 text-amber-100',
  'Show Guide': 'bg-green-600/80 text-green-100',
};

const ArticlesPage = () => {
  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Articles | Simon Nomis Live - Magic Shows Orlando</title>
        <meta name="description" content="Read articles about live magic, Orlando entertainment, mentalism, and behind-the-scenes stories from Simon Nomis Live." />
        <meta name="keywords" content="magic articles, Orlando entertainment blog, mentalism, Simon Nomis, magic show tips, Orlando date night ideas" />
        <link rel="canonical" href="https://www.simonnomislive.com/articles" />
        <meta property="og:title" content="Articles | Simon Nomis Live" />
        <meta property="og:description" content="Read articles about live magic, Orlando entertainment, and behind-the-scenes stories from Simon Nomis Live." />
        <meta property="og:url" content="https://www.simonnomislive.com/articles" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-black via-purple-900/20 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-testid="articles-heading">
              <span className="text-purple-400">Articles</span> & Insights
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Stories from behind the curtain, Orlando guides, and everything you need to know about the world of live magic.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => {
              const CardWrapper = article.link ? Link : 'div';
              const wrapperProps = article.link ? { to: article.link } : {};
              return (
              <CardWrapper
                {...wrapperProps}
                key={article.id}
                data-testid={`article-card-${article.id}`}
                className="group bg-gray-900/50 rounded-lg border border-gray-800 overflow-hidden hover:border-purple-500/50 transition-all duration-300 hover:-translate-y-1 block"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[article.category] || 'bg-gray-600 text-gray-200'}`}>
                      {article.category}
                    </span>
                    <span className="text-xs text-gray-500">{article.readTime}</span>
                  </div>
                  <h2 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors duration-200">
                    {article.title}
                  </h2>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{article.date}</span>
                    <span className="text-purple-400 text-sm font-semibold group-hover:translate-x-1 transition-transform duration-200">
                      Read More &rarr;
                    </span>
                  </div>
                </div>
              </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Experience the <span className="text-purple-400">Magic</span>?
            </h2>
            <p className="text-gray-300 mb-8 text-lg">
              Stop reading about it and come see it live. Book your tickets for Mental Hop Scotch today.
            </p>
            <Link
              to="/#tickets"
              className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105 hover:opacity-90"
              style={{ backgroundColor: 'rgb(181, 36, 189)' }}
              data-testid="articles-cta-button"
            >
              Get Tickets
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticlesPage;
